import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { debounceEvent, getComponentIndex } from "../../utils/utils";

/**
 * @name Input
 * @description Enables native inputs to be used within a Form field.
 * @category Form Inputs
 * @tags input, form
 * @example <goat-input placeholder="Enter your name"></goat-input>
 */
@Component({
  tag: "signet-input",
  styleUrl: "signet-text-input.scss",
  shadow: true,
})
export class SignetInput implements ComponentInterface {
  gid: string = getComponentIndex();

  /**
   * The input field name.
   */
  @Prop() name = `signet-input-${this.gid}`;

  /**
   * The input field placeholder.
   */
  /**
   * The input field value.
   */
  @Prop({ mutable: true }) value?: string = "";

  @Prop({ mutable: true }) modelValue?: string;

  /**
   * The input field size.
   * Possible values are: `"sm"`, `"md"`, `"lg"`. Defaults to `"md"`.
   */
 
   @Prop() size: 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large' = 'md';
 
   private get normalizedSize(): 'sm' | 'md' | 'lg' {
     switch (this.size) {
       case 'small':
         return 'sm';
       case 'medium':
         return 'md';
       case 'large':
         return 'lg';
       case 'sm':
       case 'md':
       case 'lg':
       default:
         return this.size as 'sm' | 'md' | 'lg';
     }
   }

  /**
   * The input state.
   * Possible values are: `"success"`, `"error"`, `"warning"`, 'default'. Defaults to `"default"`.
   */
  @Prop({ reflect: true }) state: "success" | "error" | "warning" | "default" =
    "default";

  /**
   * The type of control to display.
   * Possible values are: `"text"`, `"password"`, `"number"`, `"email"`, `"tel"`. Defaults to `"text"`.
   */
  @Prop() type: "text" | "password" | "number" | "email" | "tel" = "text";

  /**
   * If true, the user cannot interact with the button. Defaults to `false`.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * If true, the user read the value cannot modify it. Defaults to `false`.
   */
  @Prop({ reflect: true }) readonly = false;

  /**
   * If true, required icon is show. Defaults to `false`.
   */
  @Prop({ reflect: true }) required = false;

  /**
   * If `true`, a clear icon will appear in the input when there is a value. Clicking it clears the input.
   */
  @Prop() clearable = false;

  /**
   * Set the amount of time, in milliseconds, to wait to trigger the `signetChange` event after each keystroke.
   */
  @Prop() debounce = 300;

  @Prop() label = "";

  /**
   * Indicates whether the value of the control can be automatically completed by the browser.
   */
  @Prop() autocomplete: "on" | "off" = "off";

  @Prop({ reflect: true, mutable: true }) configAria: any = {};

  @Prop() hasButton = false;

  @Prop() buttonText = "";

  @Prop() placeholder = "";

  /**
   * The color of the required asterisk in the label. Defaults to 'red'.
   */
  @Prop() requiredAsteriskColor = 'red';

  /**
   * Emitted when a keyboard input occurred.
   */
  @Event({ eventName: "signetInput" }) signetInput: EventEmitter;

  /**
   * Emitted when the value has changed.
   */
  @Event({ eventName: "signetChange" }) signetChange: EventEmitter;

  /**
   * Emitted when the input loses focus.
   */
  @Event({ eventName: "signetBlur" }) signetBlur: EventEmitter;

  /**
   * Emitted when the input has focus.
   */
  @Event({ eventName: "signetFocus" }) signetFocus: EventEmitter;

  @Element() elm!: HTMLElement;
  private nativeInput?: HTMLInputElement;
  private tabindex?: string | number;

  @State() startSlotHasContent = false;
  @State() endSlotHasContent = false;
  @State() hasFocus = false;
  @State() labelBgColor = 'transparent';

  private computeEffectiveBackground(el: HTMLElement | null): string {
    while (el) {
      const bg = getComputedStyle(el).backgroundColor;
      if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
        
        const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
          const [, r, g, b] = match;
          return `rgb(${r}, ${g}, ${b})`;
        }
  
        return bg; // fallback if no match
      }
      el = el.parentElement;
    }
    return 'transparent';
  }

  private inputHandler = (ev: Event) => {
    const input = ev.target as HTMLInputElement | null;
    const oldValue = this.value;
    if (input) {
      this.value = input.value;
    }
    this.signetInput.emit(ev as KeyboardEvent);
    if (oldValue !== this.value) {
      this.signetChange.emit(this.value);

      // Dispatch native 'change' event for form compatibility
      this.elm.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: this.value
      }));

      // Vue 2: emit 'input' event
      this.elm.dispatchEvent(new CustomEvent('input', {
        bubbles: true,
        composed: true,
        detail: this.value
      }));

      // Vue 3: emit 'update:modelValue' event
      this.elm.dispatchEvent(new CustomEvent('update:modelValue', {
        bubbles: true,
        composed: true,
        detail: this.value
      }));
    }
  };

  private keyDownHandler = (ev: KeyboardEvent) => {
    if (ev.key === "Escape" && this.clearable) {
      this.clearInput(ev);
    }
  };

  private blurHandler = (ev: FocusEvent) => {
    this.hasFocus = false;
    this.signetBlur.emit(ev);
  };

  private focusHandler = (ev: FocusEvent) => {
    this.hasFocus = true;
    this.labelBgColor = this.computeEffectiveBackground(this.elm);
    this.signetFocus.emit(ev);
    
  };

  private clearInput = (evt: Event) => {
    this.nativeInput.value = "";
    this.inputHandler(evt);
  };

  @Method()
  async getComponentId() {
    return this.gid;
  }

  /**
   * Sets focus on the native `input` in `signet-input`. Use this method instead of the global
   * `input.focus()`.
   */
  @Method()
  async setFocus() {
    if (this.nativeInput) {
      this.nativeInput.focus();
      this.hasFocus = true;
    }
  }

  /**
   * Sets blur on the native `input` in `signet-input`. Use this method instead of the global
   * `input.blur()`.
   */
  @Method()
  async setBlur() {
    if (this.nativeInput) {
      this.nativeInput.blur();
      this.hasFocus = false;
    }
  }

  /**
   * Update the native input element when the value changes
   */
  @Watch("value")
  protected valueChanged() {
    const value = this.value;
    if (this.type === "number") {
      if (value) this.value = JSON.parse(value + "");
    }
  }

  @Watch("debounce")
  protected debounceChanged() {
    this.signetChange = debounceEvent(this.signetChange, this.debounce);
  }

  @Watch('modelValue')
  handleModelValueChange(newValue: string) {
    this.value = newValue;
  }

  connectedCallback() {
    this.debounceChanged();
  }

  private getValue(): string {
    return (this.value || "").toString();
  }

  private hasValue(): boolean {
    return this.getValue().length > 0;
  }

  // <div class="search-input-container">
  //                 <div class="input-icon">
  //                   <signet-icon icon="search" class="search-icon" />
  //                   <input
  //                     type="text"
  //                     class="search-input"
  //                     placeholder="Search"
  //                     value={this.searchTerm}
  //                     onInput={(e) => {
  //                       this.handleInputChange(e);
  //                     }}
  //                   />
  //                 </div>
  //                 <button
  //                   class="search-button"
  //                   onClick={() => this.handleSearchData()}
  //                 >
  //                   SEARCH
  //                 </button>
  //               </div>

  componentDidLoad() {
    this.labelBgColor = this.computeEffectiveBackground(this.elm);
  }

  render() {
    const inputClasses = {
      "signet-input input input-native": true,
      [`signet-input--${this.normalizedSize}`]: true,
       "has-focus": this.hasFocus,
       disabled: this.disabled,
    };
    return (
      <Host has-focus={this.hasFocus} has-value={this.hasValue()}>
        <div class="overral-container">
          <div
            class={{
              "input-container": true,
              disabled: this.disabled,
              "has-focus": this.hasFocus,
            }}
          >
            <input
              class={inputClasses}
              name={this.name}
              ref={(input) => (this.nativeInput = input)}
              type={this.type}
              autocomplete={this.autocomplete}
              value={this.value}
              tabindex={this.tabindex}
              readonly={this.readonly}
              required={this.required}
              onKeyDown={this.keyDownHandler}
              onInput={this.inputHandler}
              onBlur={this.blurHandler}
              onFocus={this.focusHandler}
              disabled={this.disabled}
              id={this.name}
              placeholder={this.placeholder}
              {...this.configAria}
            />
            {this.label.length ? (
              <label
                class={{
                  "open-label": this.value.length > 0,
                  "label": this.value.length === 0,
                  "invisible": this.placeholder.length > 0 && !this.hasFocus && this.value.length === 0,
                }}
                htmlFor={this.name}
              >
                {this.hasFocus || this.value.length > 0  ? (
                  <span
                    class="floating-label-bg"
                    style={{ backgroundColor: this.labelBgColor}}
                  ></span>
                ) : (
                  ""        
                )}
                {this.label}
                {this.required && (
                  <span style={{ color: this.requiredAsteriskColor, marginLeft: '2px' }}>*</span>
                )}
              </label>
            ) : (
              ""
            )}
          </div>
          {this.hasButton && this.buttonText.length > 0 && (
            <button
              class="search-button"
              // onClick={() => this.handleSearchData()}
            >
              {this.buttonText}
            </button>
          )}
        </div>
      </Host>
    );
  }
}